import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";

import classes from "./StudioWindow.module.css";
import mainClasses from "../BookingPage/BookingDetails/BookingDetailsMain.module.css";
import dropdownStyles from "../../pages/StudioProfile/Dropdowns.module.css";

import { useFetchUserBookingDetails } from "../../hooks/useFetchUserBookingDeails";
import { useFetchUserStudiosIds } from "../../hooks/useFetchUserStudiosIds";
import { useCreativePackage } from "../../hooks/creative/index";
import { useBookingUpdate } from "../../hooks/useBookingUpdate";
import BookingDetailsCard from "../../pages/BookingsPage/BookingDetails/BookingDetailsCard";
import ChatLink from "../ChatLink";
import { SmallModal } from "../UI/SmallModal/SmallModal";
import SaveNotification from "../UI/SaveNotification/SaveNotification";
import { NavBarContext } from "../../App";
import { resolvePaidEquipmentImageSrc, formatPricePerPiece } from "../../pages/StudioProfile/paidEquipmentHelpers";
import { getExactLinkedBookingId } from "../../util/bookingLinking";
import noUserImage from "../../public/fakeUser/noUser.webp";
import { hasBookingStartPassed } from "../../util/bookingDateTime";
import { notifyErrorToast } from '../../util/notifyActionError';

function isBookingExpired(createdAt) {
    if (!createdAt) return false;
    const created = new Date(createdAt);
    const now = new Date();
    return (now - created) / (1000 * 60 * 60) > 24;
}

function rulesLinesFromHouseRules(houseRules) {
    if (!houseRules) return [];
    const HOUSE_RULE_COPY = {
        smokingAllowed: {
            allowed: "Rauchen & Dampfen erlaubt",
            blocked: "Rauchen & Dampfen nicht erlaubt",
        },
        alcoholAllowed: {
            allowed: "Alkoholkonsum erlaubt",
            blocked: "Alkoholkonsum nicht erlaubt",
        },
        foodAllowed: {
            allowed: "Essen im Studiobereich erlaubt",
            blocked: "Essen im Studiobereich nicht erlaubt",
        },
        adultContentAllowed: {
            allowed: "Erwachsenen-Inhalte oder Nacktdrehs erlaubt",
            blocked: "Erwachsenen-Inhalte oder Nacktdrehs nicht erlaubt",
        },
        fogMachineAllowed: {
            allowed: "Nebelmaschinen erlaubt",
            blocked: "Nebelmaschinen nicht erlaubt",
        },
        glitterConfettiAllowed: {
            allowed: "Glitzer, Konfetti oder kleine Partikel erlaubt",
            blocked: "Glitzer, Konfetti oder kleine Partikel nicht erlaubt",
        },
        pyrotechnicsAllowed: {
            allowed: "Flammen & Pyrotechnik erlaubt",
            blocked: "Flammen & Pyrotechnik nicht erlaubt",
        },
        equipmentChangesAllowed: {
            allowed: "Änderungen an der Studio-Ausstattung erlaubt",
            blocked: "Änderungen an der Studio-Ausstattung nicht erlaubt",
        },
    };
    const lines = Object.entries(HOUSE_RULE_COPY)
        .filter(([key]) => typeof houseRules[key] === "boolean")
        .map(([key, text]) => (houseRules[key] ? text.allowed : text.blocked));
    const custom = houseRules.customRules && String(houseRules.customRules).trim();
    if (custom) {
        lines.push(`Zusätzliche Regeln: ${custom.charAt(0).toUpperCase()}${custom.slice(1)}`);
    }
    return lines;
}

function getCancellationPolicyConfig(rawValue) {
    if (!rawValue) return null;
    const CANCELLATION_POLICY_COPY = {
        FLEXIBLE: {
            title: "Kulant",
            lines: [
                { prefix: "Bis 48 Stunden vor Beginn: ", highlight: "Volle Rückerstattung" },
                { prefix: "Bis 24 Stunden vor Beginn: ", highlight: "50% Rückerstattung" },
                { prefix: "Unter 24 Stunden vor Beginn: ", highlight: "Keine Rückerstattung" },
            ],
        },
        STANDARD: {
            title: "Standard",
            lines: [
                { prefix: "Bis 5 Tage vor Beginn: ", highlight: "Volle Rückerstattung" },
                { prefix: "Bis 48 Stunden vor Beginn: ", highlight: "50% Rückerstattung" },
                { prefix: "Unter 48 Stunden vor Beginn: ", highlight: "Keine Rückerstattung" },
            ],
        },
        STRICT: {
            title: "Strikt",
            lines: [
                { prefix: "Bis 14 Tage vor Beginn: ", highlight: "Volle Rückerstattung" },
                { prefix: "Bis 7 Tage vor Beginn: ", highlight: "50% Rückerstattung" },
                { prefix: "Unter 7 Tage vor Beginn: ", highlight: "Keine Rückerstattung" },
            ],
        },
    };
    const trimmed = rawValue.trim();
    const upper = trimmed.toUpperCase();
    if (CANCELLATION_POLICY_COPY[upper]) return CANCELLATION_POLICY_COPY[upper];
    const lower = trimmed.toLowerCase();
    if (lower === "kulant") return CANCELLATION_POLICY_COPY.FLEXIBLE;
    if (lower === "standard") return CANCELLATION_POLICY_COPY.STANDARD;
    if (lower === "strikt") return CANCELLATION_POLICY_COPY.STRICT;
    return null;
}

export const StudioWindow = ({
    isStudioWindowOpen,
    setIsStudioWindowOpen,
    activeConversation,
    userId,
    isConversationResolving = false,
}) => {
    const { activeView, setActiveView } = useContext(NavBarContext);
    const [isMobile, setIsMobile] = useState(false);
    const [isPriceExpanded, setIsPriceExpanded] = useState(false);
    const [isProjectExpanded, setIsProjectExpanded] = useState(false);
    const [isExpandedCancellation, setIsExpandedCancellation] = useState(false);
    const [isRulesExpanded, setIsRulesExpanded] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState(null);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");

    const { updateBookingStatus, isLoading: isBookingUpdateLoading } = useBookingUpdate();

    const studio = activeConversation?.studio || null;

    const { data: studiosIdsData } = useFetchUserStudiosIds(userId);
    const userStudioIds = studiosIdsData?.studiosByUserID?.items?.map((s) => s.id) || [];
    const ownsStudioInChat = Boolean(activeConversation?.studioID && userStudioIds.includes(activeConversation.studioID));

    const linkedBookingId = getExactLinkedBookingId(activeConversation);
    const bookingRowId = linkedBookingId || null;

    const { bookingData: booking, loading: bookingDetailLoading } = useFetchUserBookingDetails(bookingRowId);

    const packageId = booking?.creativePackages?.[0]?.packageID;
    const { package: packageData } = useCreativePackage(packageId);

    const showHostPendingFooter =
        ownsStudioInChat &&
        booking?.status === "PENDING" &&
        !isBookingExpired(booking?.createdAt) &&
        !hasBookingStartPassed(booking?.startDateTime);

    const handleProfileNavigation = () => {
        if (activeView === "creator") setActiveView("creator");
    };

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 600);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    const formatPrice = (value) => {
        const num = Number(value) || 0;
        if (Number.isInteger(num)) return String(num);
        return String(num).replace(".", ",");
    };

    const calculateBookingDuration = (startDateTime, endDateTime) => {
        if (!startDateTime || !endDateTime) return 0;
        const start = new Date(startDateTime);
        const end = new Date(endDateTime);
        return Math.round(((end - start) / (1000 * 60 * 60)) * 100) / 100;
    };

    const formatBookingDuration = (startDateTime, endDateTime) => {
        if (!startDateTime || !endDateTime) return "0 Stunden";
        const duration = calculateBookingDuration(startDateTime, endDateTime);
        return `${Math.round(duration)} Stunden`;
    };

    const calculateBaseStudioPrice = () => {
        if (booking?.totalStudioPrice == null) return null;
        const discount = Number(booking?.appliedDiscount) || 0;
        const surcharge = Number(booking?.appliedSurcharge) || 0;
        const base = Number(booking.totalStudioPrice) + discount - surcharge;
        return Math.max(0, base);
    };

    const cancellationPolicy = booking?.Studio?.cancellationPolicy || "";
    const cancellationPolicyConfig = getCancellationPolicyConfig(cancellationPolicy);
    const projectDescription = booking?.projectDescription || "";
    const characterLimit = isMobile ? 90 : 150;

    const cancellationPreview =
        isExpandedCancellation || cancellationPolicy.length <= characterLimit
            ? cancellationPolicy
            : `${cancellationPolicy.slice(0, characterLimit)}...`;

    const projectPreview =
        isProjectExpanded || projectDescription.length <= characterLimit
            ? projectDescription
            : `${projectDescription.slice(0, characterLimit)}...`;

    const rulesLines = rulesLinesFromHouseRules(booking?.Studio?.houseRules);
    const visibleRules = isRulesExpanded ? rulesLines : rulesLines.slice(0, 2);

    const openDecisionModal = (mode) => {
        setModalMode(mode);
        setModalOpen(true);
    };

    const closeDecisionModal = () => {
        if (isBookingUpdateLoading) return;
        setModalOpen(false);
        setModalMode(null);
    };

    const handleAcceptBooking = async () => {
        try {
            await updateBookingStatus(booking?.id, "CONFIRMED");
            setNotificationMessage("Buchung bestätigt");
            setShowNotification(true);
            closeDecisionModal();
        } catch {
            notifyErrorToast('Es gab ein Problem beim Bestätigen der Buchung. Bitte versuche es später erneut.', {
              title: 'Buchung konnte nicht bestätigt werden',
            });
        }
    };

    const handleRejectBooking = async () => {
        try {
            await updateBookingStatus(booking?.id, "DECLINED");
            setNotificationMessage("Buchung abgelehnt");
            setShowNotification(true);
            closeDecisionModal();
        } catch {
            notifyErrorToast('Es gab ein Problem beim Ablehnen der Buchung. Bitte versuche es später erneut.', {
              title: 'Buchung konnte nicht abgelehnt werden',
            });
        }
    };

    const fromCreatorPage = ownsStudioInChat;

    const showGuestGesamtbetrag = Boolean(
        booking &&
            !fromCreatorPage &&
            booking.totalPrice != null &&
            String(booking.totalPrice).trim() !== ""
    );

    const bookingDetailsLoading = Boolean(bookingRowId && bookingDetailLoading && !booking);
    const waitingForBookingId = bookingDetailsLoading;
    const bookedEquipmentItems = Array.isArray(booking?.bookedEquipment) ? booking.bookedEquipment : [];
    const studioPaidEquipmentItems = Array.isArray(booking?.Studio?.paidEquipmentItems?.items)
        ? booking.Studio.paidEquipmentItems.items
        : [];
    const bookedEquipmentCards = bookedEquipmentItems.map((item) => {
        const matchedStudioItem = studioPaidEquipmentItems.find(
            (studioItem) => studioItem?.id === item?.equipmentItemId
        );
        return {
            ...item,
            imageKey: matchedStudioItem?.imageKey || null,
            price: matchedStudioItem?.price,
        };
    });
    const getBookedEquipmentUnitPrice = (equipmentItemId) => {
        const matchedStudioItem = studioPaidEquipmentItems.find((studioItem) => studioItem?.id === equipmentItemId);
        const price = Number(matchedStudioItem?.price);
        return Number.isFinite(price) ? price : null;
    };
    
    return (
        <div className={`${classes.container} ${isStudioWindowOpen ? classes.open : classes.closed}`}>
            <header className={classes.header}>
                <h1 className="titleSemibold3">Buchung</h1>
                <button className={`${classes.closeButton} semiboldXs`} onClick={() => setIsStudioWindowOpen(false)}>
                    Details verbergen
                    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" viewBox="0 0 11 12" fill="none">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M4.9994 11.6567L4.29229 10.9496L9.24204 5.99988L4.29229 1.05014L4.99939 0.34303L10.6562 5.99988L4.9994 11.6567Z"
                            fill="#696F76"
                        />
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M0.999395 5.46143L9.9994 5.46143L9.9994 6.46143L0.999395 6.46143L0.999395 5.46143Z"
                            fill="#696F76"
                        />
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M4.9994 11.6567L4.29229 10.9496L9.24204 5.99988L4.29229 1.05014L4.99939 0.34303L10.6562 5.99988L4.9994 11.6567Z"
                            stroke="#696F76"
                            strokeWidth="0.4"
                        />
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M0.999395 5.46143L9.9994 5.46143L9.9994 6.46143L0.999395 6.46143L0.999395 5.46143Z"
                            stroke="#696F76"
                            strokeWidth="0.4"
                        />
                    </svg>
                </button>
            </header>
            {!activeConversation ? (
                <div className={classes.noConversation}>
                    {isConversationResolving ? (
                        <div className={classes.loadingSpinner} />
                    ) : (
                        <p>Bitte wähle eine Konversation aus, um Details zu sehen.</p>
                    )}
                </div>
            ) : (
                <>
                    <div className={classes.content}>
                        {booking ? (
                            <div className={classes.bookingDetailsCardContainer} style={{ width: "100%" }}>
                                <BookingDetailsCard
                                    bookingData={booking}
                                    packageData={packageData}
                                    fromCreatorPage={fromCreatorPage}
                                    fromMessagesPage={true}
                                    loading={waitingForBookingId || bookingDetailsLoading}
                                    suppressInlineActions
                                />

                                {showGuestGesamtbetrag && (
                                  <div className={classes.priceContainerWrapper}>
                                    <div
                                        className={`${mainClasses.priceContainer} ${classes.mainSection} ${
                                            isPriceExpanded ? mainClasses.expanded : ""
                                        }`}
                                    >
                                        {isPriceExpanded &&
                                            (() => {
                                                const duration = formatBookingDuration(
                                                    booking?.startDateTime,
                                                    booking?.endDateTime
                                                );
                                                const numericDuration = calculateBookingDuration(
                                                    booking?.startDateTime,
                                                    booking?.endDateTime
                                                );
                                                const baseStudioPrice = calculateBaseStudioPrice();
                                                const totalStudioPrice = Number(booking?.totalStudioPrice) || 0;
                                                const appliedDiscount = Number(booking?.appliedDiscount) || 0;
                                                const appliedSurcharge = Number(booking?.appliedSurcharge) || 0;
                                                const appliedFees = Array.isArray(booking?.appliedFees)
                                                    ? booking.appliedFees
                                                    : [];
                                                const totalEquipmentPrice = Number(booking?.totalEquipmentPrice) || 0;
                                                const creativePackages = Array.isArray(booking?.creativePackages)
                                                    ? booking.creativePackages
                                                    : [];
                                                const bookedEquipment = Array.isArray(booking?.bookedEquipment)
                                                    ? booking.bookedEquipment
                                                    : [];
                                                const hasCompleteEquipmentPricing = bookedEquipment.length > 0
                                                    && bookedEquipment.every(
                                                        (item) => getBookedEquipmentUnitPrice(item?.equipmentItemId) != null
                                                    );
                                                const effectiveBaseStudioPrice =
                                                    baseStudioPrice != null ? baseStudioPrice : totalStudioPrice;
                                                const studioHourly =
                                                    numericDuration > 0 && effectiveBaseStudioPrice
                                                        ? Math.round(
                                                              (effectiveBaseStudioPrice / numericDuration) * 100
                                                          ) / 100
                                                        : booking?.Studio?.pricePerHour || null;

                                                const discountRules = booking?.Studio?.discountRules || [];
                                                let discountLabel = "Rabatt";
                                                if (appliedDiscount > 0 && discountRules.length && totalStudioPrice > 0) {
                                                    const baseForRule = totalStudioPrice + appliedDiscount;
                                                    const matchingRule = discountRules.find((rule) => {
                                                        const minHours = Number(rule?.minHours) || 0;
                                                        if (numericDuration < minHours) return false;
                                                        const value = Number(rule?.discountValue) || 0;
                                                        if (!value) return false;
                                                        let expected = 0;
                                                        if (rule?.discountType === "PERCENTAGE") {
                                                            expected = Math.round((baseForRule * value) / 100);
                                                        } else if (rule?.discountType === "ABSOLUTE") {
                                                            expected = value;
                                                        } else return false;
                                                        return Math.abs(expected - appliedDiscount) <= 1;
                                                    });
                                                    if (matchingRule) {
                                                        const value = Number(matchingRule.discountValue) || 0;
                                                        const minHours = Number(matchingRule.minHours) || 0;
                                                        const valueLabel =
                                                            matchingRule.discountType === "PERCENTAGE"
                                                                ? `${value}%`
                                                                : `${formatPrice(value)} €`;
                                                        discountLabel = `Rabatt (${valueLabel}${
                                                            minHours ? ` ab ${minHours} Stunden` : ""
                                                        })`;
                                                    }
                                                }

                                                return (
                                                    <div className={mainClasses.priceBreakdown}>
                                                        {effectiveBaseStudioPrice > 0 && (
                                                            <div className={mainClasses.breakdownItem}>
                                                                <span className="regularLarge">
                                                                    {booking?.packageName
                                                                        ? "Paketpreis"
                                                                        : studioHourly
                                                                        ? `${studioHourly} € x ${numericDuration} Stunden`
                                                                        : `€${booking?.Studio?.pricePerHour} x ${duration}`}
                                                                </span>
                                                                <span style={{ whiteSpace: "nowrap" }} className="regularLarge">
                                                                    €{formatPrice(effectiveBaseStudioPrice)}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {appliedDiscount > 0 && (
                                                            <div className={mainClasses.breakdownItem}>
                                                                <span className="regularLarge">{discountLabel}</span>
                                                                <span style={{ whiteSpace: "nowrap" }} className="regularLarge">
                                                                    -€{formatPrice(appliedDiscount)}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {appliedSurcharge > 0 && (
                                                            <div className={mainClasses.breakdownItem}>
                                                                <span className="regularLarge">Wochenendaufschlag</span>
                                                                <span style={{ whiteSpace: "nowrap" }} className="regularLarge">
                                                                    +€{formatPrice(appliedSurcharge)}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {appliedFees.map((fee, index) => (
                                                            <div className={mainClasses.breakdownItem} key={fee?.name || index}>
                                                                <span className="regularLarge">{fee?.name || "Gebühr"}</span>
                                                                <span style={{ whiteSpace: "nowrap" }} className="regularLarge">
                                                                    +€{formatPrice(Number(fee?.amount) || 0)}
                                                                </span>
                                                            </div>
                                                        ))}
                                                        {creativePackages.map((pkg, index) => {
                                                            const quantity = Number(pkg?.quantity) || 0;
                                                            const totalPrice = Number(pkg?.price) || 0;
                                                            if (!totalPrice) return null;
                                                            const unitPrice = quantity > 0 ? totalPrice / quantity : null;
                                                            return (
                                                                <div
                                                                    className={mainClasses.breakdownItem}
                                                                    key={pkg?.packageID || pkg?.packageName || index}
                                                                >
                                                                    <span className="regularLarge">
                                                                        {pkg?.packageName || "Creative-Paket"}
                                                                        {unitPrice
                                                                            ? `: ${formatPrice(unitPrice)} € x ${quantity} Stunden`
                                                                            : ""}
                                                                    </span>
                                                                    <span style={{ whiteSpace: "nowrap" }} className="regularLarge">
                                                                        +€{formatPrice(totalPrice)}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                        {bookedEquipment.length > 0 && (
                                                            <>
                                                                {bookedEquipment.map((item, index) => (
                                                                    <div
                                                                        className={mainClasses.breakdownItem}
                                                                        key={item?.equipmentItemId || item?.name || index}
                                                                    >
                                                                        <span className="regularLarge">
                                                                            {item?.name || "Equipment"} x {item?.quantity || 1}
                                                                        </span>
                                                                        {(() => {
                                                                            const quantity = Number(item?.quantity) || 1;
                                                                            const unitPrice = getBookedEquipmentUnitPrice(
                                                                                item?.equipmentItemId
                                                                            );
                                                                            if (unitPrice == null) return null;
                                                                            return (
                                                                                <span
                                                                                    style={{ whiteSpace: "nowrap" }}
                                                                                    className="regularLarge"
                                                                                >
                                                                                    +€{formatPrice(unitPrice * quantity)}
                                                                                </span>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                ))}
                                                                {totalEquipmentPrice > 0 && !hasCompleteEquipmentPricing && (
                                                                    <div className={mainClasses.breakdownItem}>
                                                                        <span className="regularLarge">Equipment gesamt</span>
                                                                        <span style={{ whiteSpace: "nowrap" }} className="regularLarge">
                                                                            +€{formatPrice(totalEquipmentPrice)}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        <div className={mainClasses.priceMainContent}>
                                            <div className={mainClasses.priceInnerContainer}>
                                                <h2
                                                    style={{ marginBottom: "0px" }}
                                                    className={`${mainClasses.cancellationTitle} semiboldMiddle`}
                                                >
                                                    Gesamtbetrag
                                                </h2>
                                                <button
                                                    type="button"
                                                    onClick={() => setIsPriceExpanded((v) => !v)}
                                                    className={mainClasses.priceDetails}
                                                >
                                                    {isPriceExpanded ? "Weniger anzeigen" : "Details sehen"}
                                                </button>
                                            </div>
                                            <p className={`${mainClasses.price} titleSemibold3`}>€{booking?.totalPrice}</p>
                                        </div>
                                    </div>
                                  </div>
                                )}

                                {showGuestGesamtbetrag && (
                                    <div className={`${mainClasses.horizontalLine} ${classes.lineBleed}`} />
                                )}

                                {bookedEquipmentCards?.length > 0 && (
                                    <div className={`${classes.equipmentBlock} ${classes.mainSection}`}>
                                        <h3 className={`titleSemibold3 ${classes.equipmentHeading}`}>
                                            Equipment
                                        </h3>
                                        <div className={`${dropdownStyles.paidEquipmentViewport} ${classes.equipmentViewport}`}>
                                            <div className={dropdownStyles.paidEquipmentTrack}>
                                                {bookedEquipmentCards.map((item, index) => {
                                                    const key = item?.equipmentItemId || item?.name || `eq-${index}`;
                                                    const title = item?.name || "Equipment";
                                                    const quantity = Number(item?.quantity) || 1;
                                                    const imageSrc = resolvePaidEquipmentImageSrc(item);
                                                    return (
                                                        <article key={key} className={dropdownStyles.paidEquipmentCard}>
                                                            <div className={dropdownStyles.paidEquipmentImageBox}>
                                                                {imageSrc ? (
                                                                    <img src={imageSrc} alt={title} />
                                                                ) : (
                                                                    <span
                                                                        className="regularMiddle"
                                                                        style={{ color: "#696F76", textAlign: "center" }}
                                                                    >
                                                                        Equipment
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <h4
                                                                className={`${dropdownStyles.paidEquipmentTitle} regularLarge ${classes.equipmentCardTitle}`}
                                                            >
                                                                {title}
                                                            </h4>
                                                            <p className={`${dropdownStyles.paidEquipmentPrice} regularSmall`}>
                                                                {item?.price != null
                                                                    ? `${formatPricePerPiece(item.price)} - ${quantity} x gebucht`
                                                                    : `${quantity} x gebucht`}
                                                            </p>
                                                        </article>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {booking && (
                                    <div className={`${mainClasses.hostContainer} ${classes.mainSectionUserInfo}`}>
                                        {!fromCreatorPage ? (
                                            <>
                                                <h2 className={`${mainClasses.hostTitle} titleSemibold3`}>Dein Host</h2>
                                                <Link
                                                    to={`/users/show/${booking?.Studio?.userID}`}
                                                    onClick={handleProfileNavigation}
                                                >
                                                    <div className={mainClasses.userProfile}>
                                                        <div className={mainClasses.userPicture}>
                                                            <img
                                                                src={
                                                                    booking?.Studio?.User?.studioOwnerProfilePicture ||
                                                                    noUserImage
                                                                }
                                                                alt="Host"
                                                            />
                                                        </div>
                                                        <div className={mainClasses.hostInnerContainer}>
                                                            <div className={mainClasses.host}>
                                                                <p className="semiboldLarge">
                                                                    Studio Host: {booking?.Studio?.User?.firstName}{" "}
                                                                    {booking?.Studio?.User?.lastName}
                                                                </p>
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="8"
                                                                    height="15"
                                                                    viewBox="0 0 8 15"
                                                                    fill="none"
                                                                >
                                                                    <path
                                                                        fillRule="evenodd"
                                                                        clipRule="evenodd"
                                                                        d="M6.74011 8.26008L7 7.95487L6.74544 7.63101L1.68535 1.19336L1 1.80438L5.81471 7.92985L1.02575 13.5539L1.68782 14.1934L6.74011 8.26008Z"
                                                                        fill="#1F1F1F"
                                                                        stroke="#1F1F1F"
                                                                        strokeWidth="0.4"
                                                                    />
                                                                </svg>
                                                            </div>
                                                            <span className="regularMiddle" style={{ color: "#696F76" }}>
                                                                Host
                                                            </span>
                                                        </div>
                                                    </div>
                                                </Link>
                                                <ChatLink
                                                    userId={booking?.Studio?.userID}
                                                    studioId={booking?.Studio?.id}
                                                    studioName={booking?.Studio?.StudioName}
                                                    studioImage={booking?.Studio?.processedImages?.[0]}
                                                    className={`${mainClasses.contactButton} semiboldXs`}
                                                    chatRoomId={booking?.chatRoomID}
                                                    bookingId={booking?.id}
                                                >
                                                    Nachricht schreiben
                                                </ChatLink>
                                            </>
                                        ) : (
                                            <>
                                                <h2 className={`${mainClasses.hostTitle} titleSemibold3`}>Dein Gast</h2>
                                                <Link
                                                    to={`/users/show/${booking?.User?.id}`}
                                                    onClick={handleProfileNavigation}
                                                >
                                                    <div className={mainClasses.userProfile}>
                                                        <div className={mainClasses.userPicture}>
                                                            <img
                                                                src={booking?.User?.userProfilePicture || noUserImage}
                                                                alt="Gast"
                                                            />
                                                        </div>
                                                        <div className={mainClasses.hostInnerContainer}>
                                                            <div className={mainClasses.host}>
                                                                <span className="semiboldLarge">
                                                                    {booking?.User?.firstName} {booking?.User?.lastName}
                                                                </span>
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="8"
                                                                    height="15"
                                                                    viewBox="0 0 8 15"
                                                                    fill="none"
                                                                >
                                                                    <path
                                                                        fillRule="evenodd"
                                                                        clipRule="evenodd"
                                                                        d="M6.74011 8.26008L7 7.95487L6.74544 7.63101L1.68535 1.19336L1 1.80438L5.81471 7.92985L1.02575 13.5539L1.68782 14.1934L6.74011 8.26008Z"
                                                                        fill="#1F1F1F"
                                                                        stroke="#1F1F1F"
                                                                        strokeWidth="0.4"
                                                                    />
                                                                </svg>
                                                            </div>
                                                            <span className="regularMiddle" style={{ color: "#696F76" }}>
                                                                Gast
                                                            </span>
                                                        </div>
                                                    </div>
                                                </Link>
                                                <ChatLink
                                                    userId={booking?.User?.id}
                                                    studioId={booking?.Studio?.id}
                                                    studioName={booking?.Studio?.StudioName}
                                                    studioImage={booking?.Studio?.processedImages?.[0]}
                                                    className={`${mainClasses.contactButton} semiboldSmall`}
                                                    chatRoomId={booking?.chatRoomID}
                                                    bookingId={booking?.id}
                                                >
                                                    Nachricht schreiben
                                                </ChatLink>
                                            </>
                                        )}
                                    </div>
                                )}

                                {booking && (booking.projectName || booking.projectDescription) && (
                                    <>
                                        <div className={`${mainClasses.horizontalLine} ${classes.lineBleed}`} />
                                        <div className={`${mainClasses.cancellationContainer} ${classes.cancellationContainerMessagesPage}`}>
                                            <h2
                                                className={`${mainClasses.cancellationTitle} titleSemibold3`}
                                            >
                                                Über das Projekt
                                            </h2>
                                            {booking.projectName && (
                                                <h3 className="regularLarge">{booking.projectName}</h3>
                                            )}
                                            {booking.projectDescription && (
                                                <>
                                                    <p
                                                        className={mainClasses.cancellationText}
                                                        onClick={() => setIsProjectExpanded((v) => !v)}
                                                        role="presentation"
                                                    >
                                                        {projectPreview}
                                                    </p>
                                                    {projectDescription.length > characterLimit && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setIsProjectExpanded((v) => !v)}
                                                            className={`${mainClasses.readMoreButton} semiboldSmall`}
                                                        >
                                                            {isProjectExpanded ? "Weniger anzeigen" : "Weiterlesen"}
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}

                                {booking && cancellationPolicy && (
                                    <>
                                        <div className={`${mainClasses.horizontalLine} ${classes.lineBleed}`} />
                                        <div className={`${mainClasses.cancellationContainer} ${classes.cancellationContainerMessagesPage}`}>
                                            <h2
                                                className={`${mainClasses.cancellationTitle} titleSemibold3`}
                                            > 
                                                Stornierungsbedingungen
                                            </h2>
                                            {cancellationPolicyConfig ? (
                                                <>
                                                    <h3 style={{fontSize: "1.125rem"}} className="mediumLarge">{cancellationPolicyConfig.title}</h3>
                                                    {cancellationPolicyConfig.lines.map((line) => (
                                                        <p key={line.prefix} className={mainClasses.cancellationText}>
                                                            {line.prefix}
                                                            <br />
                                                            <span className="semiboldSmall">{line.highlight}</span>
                                                        </p>
                                                    ))}
                                                </>
                                            ) : (
                                                <>
                                                    <p
                                                        className={mainClasses.cancellationText}
                                                        onClick={() => setIsExpandedCancellation((v) => !v)}
                                                        role="presentation"
                                                    >
                                                        {cancellationPreview}
                                                    </p>
                                                    {cancellationPolicy.length > characterLimit && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setIsExpandedCancellation((v) => !v)}
                                                            className={`${mainClasses.readMoreButton} semiboldSmall`}
                                                        >
                                                            {isExpandedCancellation ? "Weniger anzeigen" : "Weiterlesen"}
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}

                                {booking && rulesLines.length > 0 && (
                                    <>
                                        <div className={`${mainClasses.horizontalLine} ${classes.lineBleed}`} />
                                        <div className={`${mainClasses.cancellationContainer} ${classes.cancellationContainerMessagesPage}`}>
                                            <h2
                                                className={`${mainClasses.cancellationTitle} titleSemibold3`}
                                            >
                                                Grundregeln
                                            </h2>
                                            <ul
                                                className={mainClasses.rulesList}
                                                onClick={() => setIsRulesExpanded((v) => !v)}
                                                role="presentation"
                                            >
                                                {visibleRules.map((line, index) => (
                                                    <li className="regularLarge" key={`${index}-${line}`}>
                                                        {line}
                                                    </li>
                                                ))}
                                            </ul>
                                            {rulesLines.length > 2 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setIsRulesExpanded((v) => !v)}
                                                    className={`${mainClasses.readMoreButton} semiboldSmall`}
                                                >
                                                    {isRulesExpanded ? "Weniger anzeigen" : "Weiterlesen"}
                                                </button>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : waitingForBookingId || bookingDetailsLoading ? (
                            <BookingDetailsCard
                                bookingData={null}
                                packageData={null}
                                fromCreatorPage={fromCreatorPage}
                                loading
                                suppressInlineActions
                            />
                        ) : (
                            <div className={classes.noBookingFallback}>
                                <p className="regularLarge" style={{ color: "var(--dark-grey-3)" }}>
                                    Für diese Unterhaltung liegt noch keine Buchung vor.
                                </p>
                                {studio?.id && (
                                    <Link to={`/studio/${studio.id}`} className={classes.studioLinkWrap}>
                                        <span className="semiboldMiddle">Studio ansehen</span>
                                    </Link>
                                )}
                            </div>
                        )}
                    </div>

                    {showHostPendingFooter && (
                        <footer className={`${classes.footer} ${classes.footerSplit}`}>
                            <button
                                type="button"
                                className={`${classes.rejectButton} mediumLarge`}
                                onClick={() => openDecisionModal("reject")}
                            >
                                Ablehnen
                            </button>
                            <button
                                type="button"
                                className={`${classes.acceptButton} mediumLarge`}
                                onClick={() => openDecisionModal("accept")}
                            >
                                Akzeptieren
                            </button>
                        </footer>
                    )}
                    {!showHostPendingFooter &&
                        !waitingForBookingId &&
                        !bookingDetailsLoading &&
                        studio &&
                        !ownsStudioInChat && (
                        <footer className={classes.footer}>
                            <Link style={{ width: "100%" }} to={`/studio/${studio.id}`}>
                                <button type="button" className={`${classes.acceptButton} mediumLarge`}>
                                    Studio buchen
                                </button>
                            </Link>
                        </footer>
                    )}

                    <SmallModal
                        fromBookingDetails
                        open={modalOpen}
                        onBackdropClick={isBookingUpdateLoading ? undefined : closeDecisionModal}
                        className={mainClasses.smallModal}
                    >
                        {modalMode === "reject" ? (
                            <div className={mainClasses.modalContent}>
                                <h2 className="titleSemibold3">Buchung ablehnen?</h2>
                                <div
                                    onClick={isBookingUpdateLoading ? undefined : closeDecisionModal}
                                    className={mainClasses.closeButton}
                                    style={{
                                        cursor: isBookingUpdateLoading ? "not-allowed" : "pointer",
                                        opacity: isBookingUpdateLoading ? 0.5 : 1,
                                    }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z"
                                            fill="#1F1F1F"
                                        />
                                    </svg>
                                </div>
                                <p className={mainClasses.modalText}>
                                    Dieser Vorgang kann nicht mehr rückgängig gemacht werden.
                                </p>
                                <div className={mainClasses.modalButtons}>
                                    <button
                                        type="button"
                                        className="mediumLarge"
                                        onClick={handleRejectBooking}
                                        disabled={isBookingUpdateLoading}
                                        style={{
                                            backgroundColor: isBookingUpdateLoading ? "var(--light-grey-2)" : "var(--black)",
                                            color: isBookingUpdateLoading ? "var(--dark-grey)" : "var(--white)",
                                            cursor: isBookingUpdateLoading ? "not-allowed" : "pointer",
                                            opacity: isBookingUpdateLoading ? 0.6 : 1,
                                        }}
                                    >
                                        {isBookingUpdateLoading ? "Wird abgelehnt..." : "Buchung ablehnen"}
                                    </button>
                                    <button
                                        type="button"
                                        className="mediumLarge"
                                        onClick={closeDecisionModal}
                                        disabled={isBookingUpdateLoading}
                                        style={{
                                            backgroundColor: "var(--light-grey-2)",
                                            border: "1px solid var(--light-grey-1)",
                                            color: "var(--dark-grey)",
                                        }}
                                    >
                                        Zurück
                                    </button>
                                </div>
                            </div>
                        ) : modalMode === "accept" ? (
                            <div className={mainClasses.modalContent}>
                                <h2 className="titleSemibold3">Buchung akzeptieren</h2>
                                <div
                                    onClick={isBookingUpdateLoading ? undefined : closeDecisionModal}
                                    className={mainClasses.closeButton}
                                    style={{
                                        cursor: isBookingUpdateLoading ? "not-allowed" : "pointer",
                                        opacity: isBookingUpdateLoading ? 0.5 : 1,
                                    }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z"
                                            fill="#1F1F1F"
                                        />
                                    </svg>
                                </div>
                                <p className={mainClasses.modalText}>
                                    Mit dieser Auswahl bestätigst du diese Buchung.
                                </p>
                                <div className={mainClasses.modalButtons}>
                                    <button
                                        type="button"
                                        className="mediumLarge"
                                        onClick={handleAcceptBooking}
                                        disabled={isBookingUpdateLoading}
                                        style={{
                                            backgroundColor: isBookingUpdateLoading ? "var(--light-grey-2)" : "var(--black)",
                                            color: isBookingUpdateLoading ? "var(--dark-grey)" : "var(--white)",
                                            cursor: isBookingUpdateLoading ? "not-allowed" : "pointer",
                                            opacity: isBookingUpdateLoading ? 0.6 : 1,
                                        }}
                                    >
                                        {isBookingUpdateLoading ? "Wird akzeptiert..." : "Buchung akzeptieren"}
                                    </button>
                                    <button
                                        type="button"
                                        className="mediumLarge"
                                        onClick={closeDecisionModal}
                                        disabled={isBookingUpdateLoading}
                                        style={{
                                            backgroundColor: "var(--light-grey-2)",
                                            border: "1px solid var(--light-grey-1)",
                                            color: "var(--dark-grey)",
                                        }}
                                    >
                                        Zurück
                                    </button>
                                </div>
                            </div>
                        ) : null}
                    </SmallModal>

                    <SaveNotification
                        show={showNotification}
                        onHide={() => setShowNotification(false)}
                        message={notificationMessage}
                    />
                </>
            )}
        </div>
    );
};

export default StudioWindow;
